import { useEffect, useState, useContext } from "react"
import { FirebaseContext } from "../Firebase"
import { samplePlan, samplePlanEs } from "./careerPlanUtils"
import { useIntl } from "react-intl"
import { v4 as uuidv4 } from "uuid"

const isEmptyMilestoneDate = (date) => !date || date === "0000-00"

// Milestones with a real startDate sort by it (ties broken by endDate). Milestones missing
// a startDate but with a real endDate sort as if endDate were their startDate, after any
// milestone with the same effective date that has a real startDate. Milestones with neither
// date always sort last. Stable sort preserves original order for any remaining ties.
const compareMilestonesByDate = (a, b) => {
  const aHasStart = !isEmptyMilestoneDate(a.startDate)
  const bHasStart = !isEmptyMilestoneDate(b.startDate)
  const aHasEnd = !isEmptyMilestoneDate(a.endDate)
  const bHasEnd = !isEmptyMilestoneDate(b.endDate)

  const aEffectiveDate = aHasStart ? a.startDate : aHasEnd ? a.endDate : null
  const bEffectiveDate = bHasStart ? b.startDate : bHasEnd ? b.endDate : null

  if (aEffectiveDate === null && bEffectiveDate === null) {
    return 0
  } else if (aEffectiveDate === null) {
    return 1
  } else if (bEffectiveDate === null) {
    return -1
  }

  if (aEffectiveDate !== bEffectiveDate) {
    return aEffectiveDate < bEffectiveDate ? -1 : 1
  }
  if (aHasStart !== bHasStart) {
    return aHasStart ? -1 : 1
  }
  if (aHasStart && bHasStart && a.endDate !== b.endDate) {
    return a.endDate < b.endDate ? -1 : 1
  }
  return 0
}

/**
 * Interface for fetching and editing the current user's career plans. Passing
 * null as plan/milestone to updateCareerPlan/updateMilestone will delete that
 * plan/milestone. Passing true for optimisticUpdate arg will update local state
 * before confirming whether write to firestore was successful.
 * 
 * @returns
 *    careerPlans - array of plans, null if loading
 *    errorMessage - if there was an error in the listener, the error message
 *    updateCareerPlan - (planId, plan, optimisticUpdate) => {planId, errorMessage}
 *    updateMilestone - (planId, milestoneId, milestone, optimisticUpdate) => errorMessage 
 */
const useCareerPlans = () => {
  const { firebase } = useContext(FirebaseContext)
  const { locale } = useIntl()
  const [plans, setPlans] = useState(null)
  const [errorMessage, setErrorMessage] = useState("")

  const handleListenerError = (e) => {
    console.error("useCareerPlans: error in snapshot listener", e)
    setErrorMessage(`Error when reading career plans - ${e.message} (code: ${e.code})`)
  }

  const updateCareerPlan = async (planId, plan, optimisticUpdate = false) => {
    if (!planId) {
      planId = uuidv4()
    }
    plan?.milestones.forEach(milestone => {
      if (!milestone.id) {
        milestone.id = uuidv4()
      }
    })
    if (plan) {
      plan.updatedAt = new Date()
    }
    const updatedPlans = [...plans]
    const planIndex = updatedPlans.findIndex(p => p.id === planId || p === planId)
    if (planIndex > -1 && plan) {
      updatedPlans[planIndex] = plan
    } else if (planIndex > -1 && !plan) {
      updatedPlans.splice(planIndex, 1)
    } else if (plan) {
      plan.createdAt = new Date()
      updatedPlans.push({...plan, id: planId})
    } else {
      return
    }
    if (optimisticUpdate) {
      setPlans(updatedPlans)
    }
    try {
      await firebase.updateCareerPlans(updatedPlans)
      if (!optimisticUpdate) {
        setPlans(updatedPlans)
      }
    } catch (e) {
      console.error("saveCareerPlan error:", e)
      return { errorMessage: `Error when updating your career plan: ${e.message} (${e.code})` }
    }
    return { planId }
  }

  const updateMilestone = async (planId, milestoneId, milestone, optimisticUpdate = false) => {
    const plan = [...plans, samplePlan].find(p => p.id === planId)
    if (!plan) {
      return
    }
    if (!milestoneId) {
      milestoneId = uuidv4()
    }
    const milestoneIndex = plan.milestones.findIndex(m => m.id === milestoneId)
    const updatedMilestones = [...plan.milestones]
    if (milestoneIndex > -1 && milestone) {
      updatedMilestones[milestoneIndex] = milestone
    } else if (milestoneIndex > -1 && !milestone) {
      updatedMilestones.splice(milestoneIndex, 1)
    } else if (milestone) {
      updatedMilestones.push({...milestone, id: milestoneId})
    } else {
      return
    }
    updatedMilestones.sort(compareMilestonesByDate)
    const updatedPlan = {...plan, milestones: updatedMilestones}
    const result = await updateCareerPlan(planId, updatedPlan, optimisticUpdate)
    return result.errorMessage
  }

  let outputtedPlans = plans
  const sampleIndex = plans?.findIndex(p => p === "sample")
  const planToInsert = locale === "es-ES" ? samplePlanEs : samplePlan
  if (typeof sampleIndex === "number" && sampleIndex > -1) {
    outputtedPlans = [...plans]
    outputtedPlans[sampleIndex] = planToInsert
  }

  useEffect(() => {
    if (!firebase) {
      return
    }
    setErrorMessage("")
    try {
      // use firebase snapshot listener to subscribe to changes in the current user's
      // career plans doc
      const disconnect = firebase.subscribeToCareerPlansChanges(null, setPlans, handleListenerError)
      return disconnect
    } catch (e) {
      handleListenerError(e)
    }
  }, [firebase, firebase?.auth?.currentUser?.uid])

  return {
    careerPlans: outputtedPlans,
    errorMessage,
    updateCareerPlan,
    updateMilestone,
  }
}

export default useCareerPlans
